import { ChevronDown } from 'lucide-react'
import { ReactNode, useRef, useState } from 'react'
import useClickAway from 'react-use/lib/useClickAway'
import useKey from 'react-use/lib/useKey'
import useKeyPress from 'react-use/lib/useKeyPress'

import { cn } from '@/utils'

const NavDropdownMenu = ({
  children,
  content
}: {
  children: ReactNode
  content: ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const closeDropdown = () => {
    setOpen(false)
  }

  const openDropdown = () => {
    setOpen(true)
  }

  const toggleDropdown = () => {
    setOpen(!open)
  }

  useClickAway(ref, closeDropdown)
  useKey(`Escape`, closeDropdown)

  const onEnter = useKeyPress(e => e.key === `Enter`)[0]
  const onSpace = useKeyPress(e => e.key === ` `)[0]

  const onSelect = (fn: () => void) => () => {
    if (onEnter || onSpace) {
      fn()
    }
  }

  return (
    <div
      className="relative"
      ref={ref}
      onMouseEnter={openDropdown}
      onMouseLeave={closeDropdown}
    >
      <button
        className="flex px-3 py-1"
        data-state={open ? `open` : `closed`}
        aria-expanded={open}
        onPointerUp={toggleDropdown}
        onKeyUp={onSelect(toggleDropdown)}
      >
        <span>{children}</span>
        <ChevronDown className={cn(`ml-1 w-4`, { 'rotate-180': open })} />
      </button>
      <div className="perspective-[2000px] absolute left-1/2 top-full z-10 -translate-x-1/2">
        {open && (
          <div
            data-state={open ? `open` : `closed`}
            className={cn(
              `mt-3 rounded-lg bg-white text-black`,
              `min-w-[150px]`,
              `origin-[top-center] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90`
            )}
          >
            <div
              data-motion={open ? `from-start` : `to-end`}
              className={cn(
                `data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out`
              )}
            >
              {content}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default NavDropdownMenu
